import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { appConfig } from '@common/config/app.config';
import { namespace } from '@common/config/constants';

import { ApiKeysPanel } from './api-keys-panel';
import { WebhooksPanel } from './webhooks-panel';

type ApiWebhooksTab = 'keys' | 'webhooks';

export function ApiWebhooksCard() {
  const { t, i18n } = useTranslation(namespace);
  const isRtl = i18n.dir(i18n.language) === 'rtl';
  const [activeTab, setActiveTab] = useState<ApiWebhooksTab>('keys');

  return (
    <Card dir={isRtl ? 'rtl' : 'ltr'}>
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as ApiWebhooksTab)}
      >
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] pb-5">
          <TabsList className={isRtl ? 'justify-end' : 'justify-start'}>
            <TabsTrigger
              value="keys"
              className="text-xs font-semibold uppercase tracking-wide data-[state=active]:text-[var(--color-accent)]"
            >
              {t('api.tabApiKeys')}
            </TabsTrigger>
            <TabsTrigger
              value="webhooks"
              className="text-xs font-semibold uppercase tracking-wide data-[state=active]:text-[var(--color-accent)]"
            >
              {t('api.tabWebhooks')}
            </TabsTrigger>
          </TabsList>

          <a
            href={`${appConfig.DOCS_ENDPOINT}/docs/quickstart`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-[var(--color-accent)] underline-offset-4 hover:underline"
          >
            {t('api.fullApiDocsLink')}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </CardHeader>

        <CardContent>
          <TabsContent value="keys" className="mt-2">
            <ApiKeysPanel />
          </TabsContent>
          <TabsContent value="webhooks" className="mt-2">
            <WebhooksPanel />
          </TabsContent>
        </CardContent>
      </Tabs>
    </Card>
  );
}

export default ApiWebhooksCard;
